const DATABASE = 'sena-offline-vaults';
const STORES = ['vaults', 'snapshots', 'manifests', 'continuities'];

export function openVaultDatabase() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('Offline storage is unavailable in this browser.')); return; }
    const request = indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => {
      for (const name of STORES) {
        if (!request.result.objectStoreNames.contains(name)) request.result.createObjectStore(name, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => db.close();
      resolve(db);
    };
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('Close older Sena tabs to update offline storage.'));
  });
}

export async function readVaultRecord(store, id) {
  const db = await openVaultDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const request = tx.objectStore(store).get(id);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => db.close();
    tx.onabort = () => { db.close(); reject(tx.error); };
  });
}

export async function listVaultRecords(store) {
  const db = await openVaultDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const request = tx.objectStore(store).getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => db.close();
    tx.onabort = () => { db.close(); reject(tx.error); };
  });
}

// Commit a complete preparation and its manifest together. Crypto/network work
// happens before this transaction, never while an IndexedDB transaction is idle.
export async function commitVaultRecords(operations, { guard, guards = guard ? [guard] : [] } = {}) {
  if (!operations.length) return;
  const db = await openVaultDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([...new Set([...operations.map(item => item.store), ...guards.map(item => item.store)])], 'readwrite');
    let committed = true;
    const write = () => { for (const operation of operations) {
      const store = tx.objectStore(operation.store);
      if (operation.delete) store.delete(operation.id);
      else if (operation.add) store.add(operation.record);
      else if (operation.expected) {
        const current = store.get(operation.record.id);
        current.onsuccess = () => {
          if (JSON.stringify(current.result) === JSON.stringify(operation.expected)) store.put(operation.record);
        };
      }
      else store.put(operation.record);
    } };
    if (guards.length) {
      let remaining = guards.length;
      for (const item of guards) {
        const current = tx.objectStore(item.store).get(item.id);
        current.onsuccess = () => {
          if (JSON.stringify(current.result || null) !== JSON.stringify(item.expected || null)) committed = false;
          remaining -= 1;
          if (remaining === 0 && committed) write();
        };
      }
    } else write();
    tx.oncomplete = () => { db.close(); resolve(committed); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error || new Error('Offline storage transaction was interrupted.')); };
  });
}
